import { ReactNode, useMemo, useState } from "react";
import { Link, useLoaderData, useRouteLoaderData } from "react-router-dom";
import { Capsule } from "../../../services/capsule/capsuleModel";
import BandDeco from "../../candidate/BandDeco";
import { loggerBuilder } from "../../../services/logger";
import { toastsWithIntl } from "../../../services/toastService";
import { capsuleService } from "../../../services/capsule/capsuleService";
import { useConfirmationWithIntl } from "../../../components/ConfirmationDialog";
import { ReturnButton } from "../../../components/ReturnButton";
import SubmitButton from "../../../components/SubmitButton";
import SuspenseVideoPlayer from "../../../components/SuspenseVideoPlayer";
import { downloadURL } from "../../../react-helpers/url";
import { QRCodeCanvas } from "qrcode.react";
import qrCodeBrand from "../../../assets/logo-2max.png";
import { Organization } from "../../../services/organization/organizationModel";
import CapsuleThumbnailDialog from "./CapsuleThumnailDialog";
import CapsuleEditDialog from "./CapsuleEditDialog";
import CapsuleEmbedDialog from "./CapsuleEmbedDialog";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import useReload from "../../../hooks/useReload";
import CandidateIcon from "../../candidate/CandidateIcon";
import { cx } from "../../../react-helpers/css";

const logger = loggerBuilder("capsule-page");

const { toastSuccess } = toastsWithIntl(["organization"]);

const CapsulePage = (): ReactNode => {
  const { t } = useTranslation();
  const { organization } = useRouteLoaderData("organization") as {
    organization: Organization;
  };
  const { capsule, video } = useLoaderData() as {
    capsule: Capsule;
    video: Promise<Blob> | null;
  };
  const { enableCapsuleById, disableCapsuleById } = capsuleService();
  const { confirm } = useConfirmationWithIntl(["organization"]);
  const reload = useReload();

  const sharedCapsuleUrl = useMemo(
    () =>
      `${window.location.origin}/employeur/${organization.slug}/${capsule.id}`,
    [capsule.id, organization.slug],
  );

  const linkedInShareURL = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(sharedCapsuleUrl)}`;

  const [showThumbnailDialog, setShowThumbnailDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showEmbedDialog, setShowEmbedDialog] = useState(false);

  const hasExpired = useMemo(
    () => new Date() > new Date(capsule.expirationDate),
    [capsule.expirationDate],
  );

  // Même organisation que la fiche profil candidat (Nicolas, 01/11) : bandeau avec titre, état et boutons ronds,
  // boutons de la vidéo au-dessus du lecteur, carte « Partager » à droite. Bandeau bleu : couleur des
  // professionnels dans la charte (orange pour les candidats).
  return (
    <div className="layout_content --bg--organization-dashboard">
      <section className="ui-band cand-band pro-band">
        <BandDeco />
        <div className="ui-band_in cand-band_in">
          <ReturnButton forcedUrl="/organization/capsules">
            {t("common:header.MY_LISTINGS")}
          </ReturnButton>
          <div className="cand-band_row">
            <div>
              <h1>{capsule.name}</h1>
              <div className="cand-band_chips">
                {capsule.disabledDate && (
                  <span className="ui-chip --off">
                    {t("organization:capsule.AD_DISABLED")}
                  </span>
                )}
                {hasExpired && (
                  <span className="ui-chip --err">
                    {t("organization:capsule.AD_EXPIRED")}
                  </span>
                )}
                {!hasExpired && !capsule.disabledDate && (
                  <span className="ui-chip --ok">
                    {t("organization:capsule.ACTIVE_UNTIL", {
                      date: format(
                        new Date(capsule.expirationDate),
                        "dd/MM/yyyy",
                      ),
                    })}
                  </span>
                )}
              </div>
            </div>
            <div className="cand-band_actions">
              {!hasExpired && (
                <SubmitButton
                  className="cand-iconbtn"
                  onClick={() =>
                    capsule.disabledDate
                      ? enableCapsuleById(capsule.id).then(() => reload())
                      : confirm(
                          "organization:disable-capsule.CONFIRM",
                          () =>
                            disableCapsuleById(capsule.id).then(() => reload()),
                          true,
                        )
                  }
                  title={t("organization:capsule.ACTIVATION_TOGGLE_TIP", {
                    action: capsule.disabledDate
                      ? t("organization:capsule.ACTIVATE")
                      : t("organization:capsule.DEACTIVATE"),
                  })}
                >
                  <CandidateIcon
                    name={capsule.disabledDate ? "unlock" : "lock"}
                  />
                </SubmitButton>
              )}
              <button
                className="cand-iconbtn"
                onClick={() => setShowEditDialog(true)}
                title={t("organization:capsule.EDIT_AD_TIP")}
                aria-label={t("organization:capsule.EDIT_AD_TIP")}
              >
                <CandidateIcon name="edit" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="ui-page pro-screen">
        <div
          className={cx([
            "cand-profile_grid",
            (!video || !!capsule.disabledDate) && "--single",
          ])}
        >
          <div className="cand-col">
            {video && !hasExpired && (
              <div className="cand-profile_actions">
                <Link className="btn--2 --btn--s" to={"record"}>
                  <CandidateIcon name="film" />
                  {t("organization:capsule.EDIT_VIDEO")}
                </Link>
                <button
                  className="btn--2 --outlined --btn--s"
                  onClick={() => setShowThumbnailDialog(true)}
                >
                  <CandidateIcon name="image" />
                  {t("organization:capsule.EDIT_THUMBNAIL")}
                </button>
              </div>
            )}

            {video && (
              <div
                className={cx([
                  "video-container cand-player",
                  !!capsule.disabledDate && "--off",
                ])}
              >
                <SuspenseVideoPlayer
                  videoPromise={video}
                  thumbnail={capsule.thumbnail}
                />
              </div>
            )}

            {!capsule.disabledDate && !video && (
              <Link to="record" className="video-placeholder cand-player">
                <canvas width="16" height="9" />
                <div>
                  <button className="btn--2">
                    {t("organization:capsule.RECORD_VIDEO")}
                  </button>
                </div>
              </Link>
            )}
          </div>

          {!!video && !capsule.disabledDate && (
            <aside className="ui-card --pad cand-sidebar">
              <h2 className="ui-st cand-share_title">
                <CandidateIcon name="share" />
                {t("candidate:share.SHARE_TITLE")}
              </h2>
              <div className="cand-share-list">
                <button
                  className="cand-share"
                  onClick={() => {
                    void navigator.clipboard
                      .writeText(sharedCapsuleUrl)
                      .then(() => {
                        toastSuccess("organization:share.LINK_COPIED");
                      });
                  }}
                >
                  <span className="cand-share_ic">
                    <CandidateIcon name="copy" />
                  </span>
                  <span className="cand-share_tx">
                    <b>{t("organization:share.COPY_LINK")}</b>
                    <span>{t("organization:share.tips.SHARE_LINK_TIP")}</span>
                  </span>
                </button>
                <button
                  className="cand-share"
                  onClick={() => setShowEmbedDialog(true)}
                >
                  <span className="cand-share_ic">
                    <CandidateIcon name="code" />
                  </span>
                  <span className="cand-share_tx">
                    <b>{t("organization:share.EMBED_CAPSULE")}</b>
                    <span>
                      {t("organization:share.tips.EMBED_CAPSULE_TIP")}
                    </span>
                  </span>
                </button>
                <a
                  className="cand-share --linkedin"
                  href={linkedInShareURL}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span className="cand-share_ic">
                    <CandidateIcon name="linkedin" />
                  </span>
                  <span className="cand-share_tx">
                    <b>{t("organization:share.SHARE_ON_LINKEDIN")}</b>
                    <span>
                      {t("organization:share.tips.SHARE_LINKEDIN_TIP")}
                    </span>
                  </span>
                </a>
                <a
                  className="cand-share"
                  href={`mailto:?body=${encodeURIComponent(sharedCapsuleUrl)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span className="cand-share_ic">
                    <CandidateIcon name="mail" />
                  </span>
                  <span className="cand-share_tx">
                    <b>{t("organization:share.SHARE_BY_EMAIL")}</b>
                    <span>{t("organization:share.tips.SHARE_EMAIL_TIP")}</span>
                  </span>
                </a>
                <button
                  className="cand-share"
                  onClick={() => {
                    const qrRef = document.getElementById(
                      "qrcode",
                    ) as HTMLCanvasElement | null;
                    if (qrRef) {
                      downloadURL(qrRef.toDataURL("image/png"), "qrcode.png");
                    } else {
                      logger.warn("qr code not found");
                    }
                  }}
                >
                  <span className="cand-share_ic">
                    <CandidateIcon name="qr" />
                  </span>
                  <span className="cand-share_tx">
                    <b>{t("organization:share.DOWNLOAD_QRCODE")}</b>
                    <span>
                      {t("organization:share.tips.DOWNLOAD_QRCODE_TIP")}
                    </span>
                  </span>
                </button>
              </div>
              <div className="cand-qr">
                <QRCodeCanvas
                  id={"qrcode"}
                  includeMargin
                  value={sharedCapsuleUrl}
                  size={150}
                  level="H"
                  imageSettings={{
                    src: qrCodeBrand,
                    height: 50,
                    width: 50,
                    excavate: true,
                  }}
                  fgColor="#0c0557"
                />
              </div>
            </aside>
          )}
        </div>
      </div>
      {showEditDialog && (
        <CapsuleEditDialog
          capsule={capsule}
          onClose={() => setShowEditDialog(false)}
        />
      )}
      {video && showThumbnailDialog && (
        <CapsuleThumbnailDialog
          capsule={capsule}
          video={video}
          onClose={() => setShowThumbnailDialog(false)}
        />
      )}
      {showEmbedDialog && (
        <CapsuleEmbedDialog
          sharedCapsuleUrl={sharedCapsuleUrl}
          onClose={() => setShowEmbedDialog(false)}
        />
      )}
    </div>
  );
};

export default CapsulePage;
